/**
 * Main App component - handles routing between screens
 */

import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useSocketConnection } from './hooks/useSocketConnection';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useSoundEffects } from './hooks/useSoundEffects';
import { useGameStore } from './stores/gameStore';
import { useSettingsStore } from './stores/settingsStore';
import { Lobby } from './components/lobby/Lobby';
import { GameBoard } from './components/game/GameBoard';
import { Chat } from './components/game/Chat';
import { InlineGameHeader } from './components/game/InlineGameHeader';
import { FlyingWordAnimation } from './components/game/FlyingWordAnimation';
import { EndGameCountdown } from './components/game/EndGameCountdown';
import { PauseOverlay } from './components/game/PauseOverlay';
import { ConnectionStatus } from './components/ui/ConnectionStatus';
import { SettingsButton } from './components/ui/SettingsButton';
import { Toasts } from './components/ui/Toasts';
import { ErrorBoundary } from './components/ui/ErrorBoundary';

export default function App() {
  // Socket connection + message handling (mounted exactly once) and keyboard shortcuts
  useSocketConnection();
  useKeyboardShortcuts();

  const status = useGameStore((s) => s.gameState?.status ?? null);
  const connected = useGameStore((s) => s.connected);
  const colorblindMode = useSettingsStore((s) => s.colorblindMode);
  const highContrast = useSettingsStore((s) => s.highContrast);

  // Apply accessibility settings on <html> so portals (animations, modals) inherit them
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.colorblind = String(colorblindMode);
    root.classList.toggle('high-contrast', highContrast);
  }, [colorblindMode, highContrast]);

  // Determine which screen to show
  const isInGame = status === 'playing' || status === 'ended';

  return (
    <ErrorBoundary>
      <div className="min-h-viewport bg-slate-50 dark:bg-slate-950">
        {/* Unified header - shows title + turn info (when in game) on same row */}
        <header className="app-header fixed top-0 left-0 right-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between z-50">
          <h1 className={`text-base landscape:text-sm font-bold text-slate-900 dark:text-white shrink-0 ${isInGame ? 'hidden sm:block' : ''}`}>
            Anagrabble
          </h1>
          {/* In-game: show turn/scores inline */}
          {isInGame && <InlineGameHeader />}
          <div className="flex items-center gap-1 sm:gap-2 ml-1 sm:ml-2 shrink-0">
            <ConnectionStatus connected={connected} />
            <SettingsButton />
          </div>
        </header>

        {/* Main content - offset by header height + safe area */}
        <main className="app-main">
          {isInGame ? <GameBoard /> : <Lobby />}
        </main>

        {/* End game countdown */}
        {isInGame && <EndGameCountdown />}

        {/* Chat panel */}
        {isInGame && <Chat />}

        {/* Flying tile animations */}
        {isInGame && <FlyingWordAnimation />}

        {/* Pause overlay */}
        {isInGame && <PauseOverlay />}

        {/* Screen reader announcements */}
        <GameAnnouncer />

        {/* Toast notifications */}
        <Toasts />
      </div>
    </ErrorBoundary>
  );
}

/**
 * Announces game events for screen readers and plays sound effects
 */
function GameAnnouncer() {
  const lastEvent = useGameStore((s) => s.lastEvent);
  const playerId = useGameStore((s) => s.playerId);
  const { status, currentTurnIndex, turnOrder, players } = useGameStore(
    useShallow((s) => ({
      status: s.gameState?.status,
      currentTurnIndex: s.gameState?.currentTurnIndex,
      turnOrder: s.gameState?.turnOrder,
      players: s.gameState?.players,
    }))
  );
  const [announcement, setAnnouncement] = useState('');
  const { playSound } = useSoundEffects();
  const prevTurnIndexRef = useRef<number | null>(null);

  // Announce event-based messages
  useEffect(() => {
    if (!lastEvent) return;

    let msg = '';
    // Read players imperatively so this effect only runs for new events
    const currentPlayers = useGameStore.getState().gameState?.players ?? [];

    switch (lastEvent.type) {
      case 'TILE_FLIPPED':
        msg = `${lastEvent.letter}, ${lastEvent.isVowel ? 'vowel' : 'consonant'}, added to middle pool.`;
        playSound('tileFlip');
        break;

      case 'WORD_CLAIMED':
        const claimerName = currentPlayers.find(p => p.id === lastEvent.playerId)?.name || 'Someone';
        msg = `${claimerName} claimed ${lastEvent.claimedWord.word}.`;
        // Trigger animation for the newly claimed word
        useGameStore.getState().addRecentlyClaimedWord(lastEvent.claimedWord.id);
        // Play stolen sound if word was stolen from current player, otherwise claim sound
        const stolenFromPlayers = lastEvent.claimedWord.stolenFrom;
        if (playerId && Array.isArray(stolenFromPlayers) && stolenFromPlayers.includes(playerId)) {
          playSound('wordStolen');
        } else {
          playSound('wordClaimed');
        }
        break;

      case 'CLAIM_REJECTED':
        msg = `Claim rejected: ${lastEvent.reason}`;
        playSound('error');
        break;

      case 'GAME_STARTED':
        msg = 'Game started!';
        break;

      case 'PLAYER_JOINED':
        msg = `${lastEvent.player.name} joined the game.`;
        break;

      case 'END_TIMER_STARTED':
        msg = 'Final countdown started!';
        break;

      case 'GAME_ENDED':
        const winner = lastEvent.winner;
        msg = winner
          ? `Game over! Winner: ${winner.name} with ${winner.score} points.`
          : 'Game over!';
        break;

      case 'GAME_PAUSED':
        msg = 'Game paused.';
        break;

      case 'GAME_RESUMED':
        const resumer = currentPlayers.find(p => p.id === lastEvent.resumedByPlayerId);
        msg = resumer ? `Game resumed by ${resumer.name}.` : 'Game resumed.';
        break;
    }

    if (msg) {
      setAnnouncement(msg);
    }
  }, [lastEvent, playerId, playSound]);

  // Announce turn changes
  useEffect(() => {
    if (status !== 'playing' || currentTurnIndex === undefined || !turnOrder || !players) {
      prevTurnIndexRef.current = null;
      return;
    }

    const currentIndex = currentTurnIndex;

    // Only announce if the turn actually changed
    if (prevTurnIndexRef.current !== null && prevTurnIndexRef.current !== currentIndex) {
      // Bounds check: ensure index is valid before accessing turnOrder
      if (currentIndex < 0 || currentIndex >= turnOrder.length) {
        prevTurnIndexRef.current = currentIndex;
        return;
      }
      const currentTurnPlayerId = turnOrder[currentIndex];
      const currentPlayer = players.find(p => p.id === currentTurnPlayerId);

      if (currentPlayer) {
        const isMyTurn = currentTurnPlayerId === playerId;
        const msg = isMyTurn
          ? "It's your turn! Flip a tile."
          : `It's ${currentPlayer.name}'s turn.`;
        setAnnouncement(msg);

        // Play chime when it becomes your turn
        if (isMyTurn) {
          playSound('yourTurn');
        }
      }
    }

    prevTurnIndexRef.current = currentIndex;
  }, [currentTurnIndex, status, turnOrder, players, playerId, playSound]);

  return (
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {announcement}
    </div>
  );
}
